import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { useLocalization } from '@shared-ui/localization-context';

import { PropertyAlternativeNameModel } from 'src/common/models/hotel/hotel-details-types';
import { PropertyInfoSectionViewModel, PropertyInfoSubSectionViewModel } from 'models';

import { UitkHeading } from 'uitk-react-text';
import { UitkLink } from 'uitk-react-link';
import { UitkUnformattedHtml } from 'uitk-react-unformatted-html';

import { RenderAmenityBody } from './render-amenity-helper';

interface ImportantInfoProps {
  page?: any;
  importantInfo?: PropertyInfoSectionViewModel;
  shouldMention?: PropertyInfoSubSectionViewModel;
  propertyAKA?: PropertyAlternativeNameModel;
  nationalRating?: PropertyInfoSubSectionViewModel;
}

export const ImportantInfo: React.FunctionComponent<ImportantInfoProps> = inject('page')(
  observer(({ page, importantInfo, shouldMention, propertyAKA, nationalRating }) => {
    if (!(importantInfo && importantInfo.title)) {
      return null;
    }

    const leftColumnClasses = [
      'uitk-cell',
      's-cell-1-1',
      'l-cell-1-3',
      'xl-cell-1-3',
      's-t-padding-three',
      'm-t-padding-three',
      'l-y-padding-six',
      'l-x-padding-three',
      'xl-y-padding-six',
      'xl-x-padding-three'
    ].join(' ');

    return (
      <>
        <div className={leftColumnClasses}>
          <UitkHeading level={2} size={600}>
            {importantInfo.title}
          </UitkHeading>
        </div>
        <div className="uitk-cell l-cell-2-3 xl-cell-2-3 l-x-padding-three xl-x-padding-three all-y-padding-three">
          <ImportantInfoContent importantInfo={importantInfo} />
          <ShouldMention shouldMention={shouldMention} />
          <NationalRating nationalRating={nationalRating} />
          <PropertyAKA propertyAKA={propertyAKA} />
          <HrefNextPage hrefNextPage={page && page.hrefNextPage} />
        </div>
      </>
    );
  })
);

export const ImportantInfoContent = ({ importantInfo }) => {
  if (!(importantInfo && importantInfo.sections && importantInfo.sections.length > 0)) {
    return null;
  }

  return (
    <>
      {importantInfo.sections.map((section, index) => (
        <>
          {section && section.body && section.body.length > 0 && (
            <div className="policies__section--text all-y-padding-three">
              <h3 className="policies__header--secondary uitk-type-heading-500 uitk-type-bold" key={`important-info-title-${index}`}>
                {section.title}
              </h3>
              {section.body.map((text, bodyIndex) => (
                <UitkUnformattedHtml>
                  <div
                    className="policies__body all-t-margin-two"
                    dangerouslySetInnerHTML={{ __html: text }}
                    key={`important-info-list-item-${bodyIndex}`}
                  />
                </UitkUnformattedHtml>
              ))}
            </div>
          )}
        </>
      ))}
    </>
  );
};

export const ShouldMention = ({ shouldMention }) => {
  if (!(shouldMention && shouldMention.title && shouldMention.body && shouldMention.body.length > 0)) {
    return null;
  }

  return (
    <div className="info-should-mention policies__section--text all-y-padding-three">
      <h3 className="info-should-mention__section__title uitk-type-heading-500 uitk-type-bold">{shouldMention.title}</h3>
      {shouldMention.body.map((text, index) => (
        <RenderAmenityBody text={text} index={index} category="should-mention" />
      ))}
    </div>
  );
};

export const NationalRating = ({ nationalRating }) => {
  if (!(nationalRating && nationalRating.title && nationalRating.body)) {
    return null;
  }

  return (
    <div className="info-national-rating policies__section--text all-y-padding-three">
      <h3 className="info-national_rating__section__title uitk-type-heading-500 uitk-type-bold">{nationalRating.title}</h3>
      {nationalRating.body.map((text, index) => (
        <RenderAmenityBody text={text} index={index} category="national-rating" />
      ))}
    </div>
  );
};
export const PropertyAKA = ({ propertyAKA }) => {
  if (!(propertyAKA && propertyAKA.sections && propertyAKA.sections.length > 0)) {
    return null;
  }

  return (
    <div className="info-property-alternative-name policies__section--text all-y-padding-three">
      <h3 className="info-property-alternative-name__section__title uitk-type-heading-500 uitk-type-bold">{propertyAKA.title}</h3>
      <div className="policies__body info-important__section__body all-t-padding-two">
        <ul className="uitk-type-list-300">
          {propertyAKA.sections.map(section => section && <li key={section.value}>{section.value}</li>)}
        </ul>
      </div>
    </div>
  );
};

export const HrefNextPage = ({ hrefNextPage }) => {
  const { formatText } = useLocalization();
  if (!hrefNextPage) {
    return null;
  }

  return (
    <UitkLink>
      <a href={hrefNextPage} className="uitk-cell all-y-padding-three" target="_blank" rel="noopener">
        {formatText('hotels.infosite.policies.allReviews')}
      </a>
    </UitkLink>
  );
};
